<template>
  <div class="chat-container-main" :style="pageBackgroundStyle">
    <div v-if="isPageLoading" class="modern-preloader-overlay">
      <div class="modern-preloader-content">
        <div class="avatar-ripple-container" :style="pulseStyle">
          <v-avatar size="90" class="preloader-avatar elevation-10">
            <img v-if="useImageLogo" :src="chatLogoSrc" />
            <v-icon v-else size="50" color="white">{{
              floatingIconName
            }}</v-icon>
          </v-avatar>
          <div class="ripple ripple-1"></div>
          <div class="ripple ripple-2"></div>
          <div class="ripple ripple-3"></div>
        </div>
        <div class="loading-text mt-8">
          در حال بارگذاری دستیار
          <span class="loading-dots"></span>
        </div>
      </div>
    </div>
    <div v-else>
      <div v-if="$vuetify.breakpoint.mdAndUp" class="desktop-bg-blur"></div>
      <v-card
        class="chat-panel"
        :class="{ 'desktop-mode': $vuetify.breakpoint.mdAndUp }"
        :style="chatContainerStyle"
      >
        <div class="chat-header px-4" :style="headerStyle">
          <v-avatar
            size="40"
            class="elevation-2"
            style="border: 2px solid rgba(255, 255, 255, 0.4)"
          >
            <img v-if="useImageLogo" :src="chatLogoSrc" />
            <v-icon v-else color="white">{{ floatingIconName }}</v-icon>
          </v-avatar>
          <div class="mx-3 flex-grow-1">
            <span class="d-block font-weight-bold white--text">{{
              assistantName || "دستیار هوشمند"
            }}</span>
            <div class="d-flex align-center">
              <span class="online-dot"></span>
              <small class="white--text opacity-70 mx-3"> آنلاین </small>
            </div>
          </div>
          <v-btn icon color="white" small @click="newChat" title="حذف">
            <v-icon small color="white">mdi-trash-can-outline</v-icon>
          </v-btn>
          <!-- <v-btn
          icon
          color="white"
          small
          @click="chatOpen = false"
          v-if="$vuetify.breakpoint.smAndDown"
          ><v-icon>mdi-close</v-icon></v-btn
        > -->
        </div>

        <div class="chat-body-flex">
          <div v-if="requiresLogin && showlogin" class="login-wrapper pa-4">
            <v-card
              class="login-card-modern mx-auto elevation-10"
              max-width="400"
              width="100%"
              :style="loginCardStyle"
            >
              <!-- هدر فرم لاگین -->
              <div class="text-center mb-6 mt-2">
                <v-avatar size="76" class="login-avatar mb-4 elevation-5">
                  <img v-if="useImageLogo" :src="chatLogoSrc" />
                  <v-icon
                    v-else
                    size="40"
                    :color="chatSettings.headerColor || 'primary'"
                  >
                    {{ floatingIconName }}
                  </v-icon>
                </v-avatar>
                <h2 class="white--text font-weight-bold text-h5 mb-2">
                  خوش آمدید
                </h2>
                <p class="white--text opacity-80 text-body-2 ma-0">
                  جهت شروع گفتگو اطلاعات خود را وارد کنید
                </p>
              </div>

              <!-- فرم دریافت اطلاعات -->
              <v-form @submit.prevent="submitNew" class="login-form px-1">
                <v-text-field
                  v-if="aiChatConfig?.hasAssistantsName"
                  v-model="info.name"
                  label="نام"
                  solo
                  flat
                  rounded
                  background-color="rgba(255, 255, 255, 0.95)"
                  class="modern-input mb-3"
                  hide-details="auto"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="grey darken-1" class="ml-1"
                      >mdi-account-outline</v-icon
                    >
                  </template>
                </v-text-field>

                <v-text-field
                  v-if="aiChatConfig?.hasAssistantsFamily"
                  v-model="info.lastName"
                  label="نام خانوادگی"
                  solo
                  flat
                  rounded
                  background-color="rgba(255, 255, 255, 0.95)"
                  class="modern-input mb-3"
                  hide-details="auto"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="grey darken-1" class="ml-1"
                      >mdi-card-account-details-outline</v-icon
                    >
                  </template>
                </v-text-field>

                <v-text-field
                  v-if="aiChatConfig?.hasAssistantsMobile"
                  v-model="info.phone"
                  label="شماره موبایل"
                  solo
                  flat
                  rounded
                  background-color="rgba(255, 255, 255, 0.95)"
                  class="modern-input mb-6"
                  dir="ltr"
                  :rules="rules['phone']"
                  hide-details="auto"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="grey darken-1" class="ml-1"
                      >mdi-cellphone</v-icon
                    >
                  </template>
                </v-text-field>

                <v-btn
                  block
                  x-large
                  rounded
                  elevation="3"
                  color="white"
                  class="start-chat-btn font-weight-bold mb-2"
                  :style="{ color: chatSettings.headerColor || '#667eea' }"
                  @click="submitNew"
                  :loading="loading"
                >
                  شروع گفتگو
                  <v-icon right size="22" class="mr-2"
                    >mdi-chat-processing-outline</v-icon
                  >
                </v-btn>
              </v-form>
            </v-card>
          </div>

          <div v-else ref="chatContainer" class="chat-messages-area pa-4">
            <div
              v-if="messages.length === 0"
              class="text-center py-10 opacity-50"
            >
              <v-icon size="64" color="grey lighten-1">{{
                floatingIconName
              }}</v-icon>
              <div class="mt-4">چطور می‌تونم کمکتون کنم؟</div>
            </div>

            <div
              v-for="(message, index) in messages"
              :key="index"
              :class="[
                'message-row mb-6',
                message.role === 'user' ? 'user-side' : 'assistant-side',
              ]"
            >
              <v-avatar
                v-if="message.role !== 'user' && $vuetify.breakpoint.mdAndUp"
                size="45"
                class="avatar-shadow"
              >
                <img v-if="useImageLogo" :src="chatLogoSrc" />
                <v-icon v-else color="grey darken-1">{{
                  floatingIconName
                }}</v-icon>
              </v-avatar>

              <div class="bubble-container">
                <div
                  :class="[
                    'msg-bubble',
                    message.role === 'user'
                      ? 'user-bubble'
                      : 'assistant-bubble',
                  ]"
                  :style="
                    message.role === 'user'
                      ? userMessageStyle
                      : assistantMessageStyle
                  "
                >
                  <div class="message-content">
                    <span
                      v-html="
                        formatMessage(
                          index === messages.length - 1 &&
                            isTyping &&
                            message.role !== 'user'
                            ? typingText
                            : message.content,
                        )
                      "
                    ></span>
                    <span
                      v-if="
                        index === messages.length - 1 &&
                        isTyping &&
                        message.role !== 'user'
                      "
                      class="typing-cursor"
                      >|</span
                    >
                  </div>
                  <div class="msg-footer">
                    <small>{{ formatTime(message.timestamp) }}</small>
                  </div>
                </div>
              </div>

              <v-avatar
                v-if="message.role === 'user' && $vuetify.breakpoint.mdAndUp"
                size="45"
                class="avatar-shadow"
                :style="userMessageStyle"
              >
                <v-icon color="white">mdi-account</v-icon>
              </v-avatar>
            </div>

            <div
              v-if="loading && !isTyping"
              class="assistant-side d-flex align-center mb-4"
            >
              <v-progress-circular
                indeterminate
                size="18"
                width="2"
                color="primary"
                class="mx-4"
              />
              <small
                class="grey--text"
                style="direction: rtl; unicode-bidi: plaintext"
                >در حال نوشتن...</small
              >
            </div>
          </div>
        </div>

        <div v-if="!requiresLogin || !showlogin" class="chat-footer-input">
          <div class="input-glass-box">
            <v-textarea
              ref="messageinput"
              v-model="userMessage"
              placeholder="پیام خود را بنویسید..."
              rows="1"
              auto-grow
              flat
              solo
              hide-details
              class="modern-textarea"
              @keydown.enter.exact.prevent="sendMessage"
            ></v-textarea>
            <v-btn
              fab
              small
              depressed
              :style="headerStyle"
              class="send-btn"
              @click="loading || isTyping ? cancelRequest() : sendMessage()"
            >
              <v-icon color="white">{{
                loading || isTyping ? "mdi-stop" : "mdi-send"
              }}</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>
<script>
export default {
  data() {
    return {
      originalBgDisplay: "",
      originalBgOpacity: "",
      isPageLoading: true,
      typingStopped: false,

      showlogin: false,
      loading: false,
      isTyping: false,
      userMessage: "",
      messages: [],
      previse_messages: [],
      typingText: "",
      typingSpeed: 30,
      typingTimeout: null,
      info: { name: "", phone: "", lastName: "" },
      chatOpen: true,
      aiChatConfig: {
        assistantId: null,
        assistant: null,
        hasAssistants: false,
        hasAssistantsName: false,
        hasAssistantsFamily: false,
        hasAssistantsMobile: false,
        chatSettings: {
          headerColor: "#667eea",
          headerColor2: "#764ba2",
          headerGradient: true,
          backGradient: true,
          // backgroundColor: "#ffffff",
          backColor: "#ffffff",
          backColor2: "#ffffff",
          backgroundImage: null,
          useImageBackground: false,
          textColor: "#333333",
          fontSize: 14,
          chatLogo: null,
          floatingIcon: "mdi-robot",
          useImageLogo: false,
        },
      },
    };
  },
  mounted() {
    const bgImage = document.getElementById("mainInHeaderBackground");
    if (bgImage) {
      this.originalBgDisplay = bgImage.style.display;
      this.originalBgOpacity = bgImage.style.opacity;

      bgImage.style.display = "none";
      bgImage.style.opacity = "0";
    }

    this.isPageLoading = true;
    this.fetch();
  },
  computed: {
    pulseStyle() {
      const color = this.chatSettings.headerGradient
        ? this.chatSettings.headerColor
        : this.chatSettings.headerColor || "#667eea";

      return {
        "--pulse-color": color,
      };
    },
    loginContainerStyle() {
      return {
        flex: 1,
        overflowY: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      };
    },
    assistantName() {
      return this.aiChatConfig?.assistantName || "دستیار هوشمند";
    },
    isLoginFormInvalid() {
      let pram = false;
      if (!this.requiresLogin) return false;
      if (this.aiChatConfig?.hasAssistantsName && !this.info.name?.trim())
        return true;
      if (this.aiChatConfig?.hasAssistantsFamily && !this.info.lastName?.trim())
        return true;
      console.log(
        this.aiChatConfig?.hasAssistantsMobile,
        !this.info.phone?.trim(),
        this.isPhone(this.info.phone?.trim()),
      );
      if (
        this.aiChatConfig?.hasAssistantsMobile &&
        (!this.info.phone?.trim() || !this.isPhone(this.info.phone?.trim()))
      )
        return true;
      return false;
    },
    chatBodyStyle() {
      const s = this.chatSettings;
      if (s.useImageBackground && s.backgroundImage) {
        return {
          backgroundImage: `url(${s.backgroundImage}) !important`,
          backgroundSize: "cover !important",
          backgroundPosition: "center",
        };
      }
      return {
        background: s.backGradient
          ? `linear-gradient(135deg, ${s.backColor} 0%, ${s.backColor2} 100%) !important`
          : `${s.backColor} !important` || "#fafafa !important",
      };
    },
    loginCardStyle() {
      const bg = this.headerStyle.background;
      return {
        background: bg,
        borderRadius: "28px",
        padding: "35px 25px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15) !important",
        backdropFilter: "blur(12px)",
      };
    },
    pulseColor() {
      const settings = this.chatSettings;
      let color = settings.headerColor || "#667eea";

      if (settings.headerGradient && settings.headerColor2) {
      }
      return color;
    },

    chatDirection() {
      return this.getTextDirection(this.currentlanguage);
    },
    chatContainerStyle() {
      return {
        direction: this.chatDirection,
        ...this.chatBodyStyle,
      };
    },
    textDirection() {
      const rtlLanguages = ["fa", "ar", "ar_ae", "ar_iq"];
      return rtlLanguages.includes(this.currentlanguage) ? "rtl" : "ltr";
    },
    locale() {
      return this.currentlanguage || this.$i18n.locale;
    },
    rules() {
      return {
        phone: (v) => this.isPhone(v) || this.$t("form.validation.phoneFormat"),
      };
    },

    assistantTitle() {
      return this.$t("chat.assistant_title") || "دستیار هوشمند";
    },

    chatSettings() {
      return this.aiChatConfig?.chatSettings || {};
    },

    headerStyle() {
      const settings = this.chatSettings;
      let background = settings.headerColor || "#667eea";

      if (settings.headerGradient && settings.headerColor2) {
        background = `linear-gradient(135deg, ${settings.headerColor} 0%, ${settings.headerColor2} 100%)`;
      }

      return {
        background,
        color: "white",
      };
    },
    loginStyle() {
      const settings = this.chatSettings;
      let background = settings.headerColor || "#667eea";

      if (settings.headerGradient && settings.headerColor2) {
        background = `linear-gradient(135deg, ${settings.headerColor} 0%, ${settings.headerColor2} 100%)`;
      }

      return {
        background,
        color: "white",
        opacity: 0.6,

        border: `2px solid ${settings.headerColor} !important;`,
      };
    },

    // chatMessagesStyle() {
    //   const settings = this.chatSettings;

    //   if (settings.useImageBackground && settings.backgroundImage) {
    //     return {
    //       backgroundImage: `linear-gradient(rgba(255,255,255,${
    //         1 - (settings.backgroundOpacity || 0.7)
    //       }), rgba(255,255,255,${
    //         1 - (settings.backgroundOpacity || 0.7)
    //       })), url(${settings.backgroundImage})`,
    //       backgroundSize: "cover",
    //       backgroundPosition: "center",
    //     };
    //   }

    //   return {
    //     background: settings.backGradient
    //       ? `linear-gradient(135deg, ${settings.backColor} 0%,${settings.backColor2} 100%)`
    //       : settings.backColor || "#fafafa",
    //   };
    // },

    userMessageStyle() {
      const settings = this.chatSettings;
      let background = settings.headerColor || "#667eea";

      if (settings.headerGradient && settings.headerColor2) {
        background = `linear-gradient(135deg, ${settings.headerColor} 0%, ${settings.headerColor2} 100%)`;
      }

      return {
        background,
        color: "white",
      };
    },

    assistantMessageStyle() {
      const settings = this.chatSettings;
      return {
        color: settings.textColor || "#333333",
        fontSize: `${settings.fontSize || 14}px`,
      };
    },

    floatingIconName() {
      return this.chatSettings.floatingIcon || "mdi-robot";
    },

    chatLogoSrc() {
      return this.chatSettings.chatLogo;
    },

    useImageLogo() {
      return this.chatSettings.useImageLogo && this.chatLogoSrc;
    },

    uniq_key() {
      return this.$route.params.key;
    },
    chatSettings() {
      return this.aiChatConfig?.chatSettings || {};
    },
    pageBackgroundStyle() {
      const c = this.chatSettings.headerColor || "#667eea";
      return {
        background: `radial-gradient(circle at 0% 0%, ${c}10 0%, #f5f7fa 100%)`,
      };
    },
    headerStyle() {
      const s = this.chatSettings;
      let bg = s.headerColor || "#667eea";
      if (s.headerGradient && s.headerColor2)
        bg = `linear-gradient(135deg, ${s.headerColor}, ${s.headerColor2})`;
      return { background: bg, color: "white" };
    },
    userMessageStyle() {
      const s = this.chatSettings;
      let bg = s.headerColor || "#667eea";
      if (s.headerGradient && s.headerColor2) {
        bg = `linear-gradient(135deg, ${s.headerColor} 0%, ${s.headerColor2} 100%)`;
      }
      return {
        background: bg,
        color: "white",
        "--tail-color": s.headerColor2,
      };
    },
    assistantMessageStyle() {
      return {
        background: "#ffffff",
        color: this.chatSettings.textColor || "#333",
        fontSize: `${this.chatSettings.fontSize || 14}px`,
        "--tail-color": "#ffffff",
      };
    },
    userFirstColor() {
      return this.chatSettings.headerColor || "#667eea";
    },
    chatMessagesStyle() {
      const s = this.chatSettings;
      if (s.useImageBackground && s.backgroundImage) {
        return {
          backgroundImage: `linear-gradient(rgba(255,255,255,${
            1 - (s.backgroundOpacity || 0.7)
          }), rgba(255,255,255,${1 - (s.backgroundOpacity || 0.7)})), url(${
            s.backgroundImage
          })`,
          backgroundSize: "cover",
        };
      }
      return {
        background: s.backGradient
          ? `linear-gradient(135deg, ${s.backColor} 0%,${s.backColor2} 100%)`
          : s.backColor || "#fafafa",
      };
    },
    floatingIconName() {
      return this.chatSettings.floatingIcon || "mdi-robot";
    },
    chatLogoSrc() {
      return this.chatSettings.chatLogo;
    },
    useImageLogo() {
      return this.chatSettings.useImageLogo && this.chatLogoSrc;
    },
    requiresLogin() {
      let login = localStorage.getItem(`loginForce_${this.uniq_key}`);
      return (
        (this.aiChatConfig?.hasAssistantsName ||
          this.aiChatConfig?.hasAssistantsFamily ||
          this.aiChatConfig?.hasAssistantsMobile) &&
        (!login || login !== "true")
      );
    },
  },
  beforeDestroy() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
  },
  methods: {
    preloadImage(src) {
      return new Promise((resolve, reject) => {
        if (!src) return resolve();
        const img = new Image();
        img.src = src;
        img.onload = resolve;
        img.onerror = reject;
        setTimeout(reject, 5000);
      });
    },
    handelSubmitting() {
      let configs = this.qrInfo.advanceSettings?.viewConfigs || {};

      const openDialog = () => {
        this.computedShow = configs.showCClubOnload;
        localStorage.setItem(`ccs_${this.uniq_key}`, "once");
      };

      if (!!configs) {
        let showCC = localStorage.getItem(`ccs_${this.uniq_key}`);

        if (configs.showCClubOnload)
          setTimeout(() => {
            !showCC && openDialog();
          }, 1200);

        if (!this.forcedJoinToClub)
          if (configs.showCClubOnload && !!configs.forcedSubmission) {
            let stored = localStorage.getItem(`ccsForce_${this.uniq_key}`);

            this.updateJoinToClub(stored ?? configs.forcedSubmission);
          }
      }
    },
    handelUpload() {
      this.$refs.fileInput.click();
    },
    getContentStyle(text) {
      if (!text) return {};

      const direction = this.detectLanguage(text, this.currentlanguage);
      const textAlign = this.getTextAlign(text, this.currentlanguage);

      return {
        direction: direction,
        textAlign: textAlign,
        unicodeBidi: "plaintext",
      };
    },
    getMessageStyle(message) {
      if (!message) return {};
      const direction = this.detectLanguage(message);
      const textAlign = this.getTextAlign(message);

      return {
        direction: direction,
        textAlign: textAlign,
        unicodeBidi: "plaintext",
      };
    },
    getMessageDirectionClass(message) {
      if (!message) return "";

      const direction = this.detectLanguage(message, this.currentlanguage);
      return `message-${direction}`;
    },

    pushField(field) {
      this.info[field].push({
        ...this.types[field],
      });
    },
    removeField(field, index) {
      this.info[field].splice(index, 1);
    },
    isPhone(phone) {
      let rgx = new RegExp(
        "^((\\+?98(0?)9|(0?9))[0-9]{2})[0-9]{3}[0-9]{4}$|^((\\+?۹۸(۰?)|(۰?۹))[۰-۹]{2})[۰-۹]{3}[۰-۹]{4}$",
      );
      return rgx.test(phone);
    },
    submitNew() {
      if (
        this.info.name.trim() === "" &&
        this.aiChatConfig?.hasAssistantsName
      ) {
        this.$toast.warning("لطفاً نام خود را وارد نمایید.");
        return;
      }
      if (
        this.info.lastName.trim() === "" &&
        this.aiChatConfig?.hasAssistantsFamily
      ) {
        this.$toast.warning("لطفاً نام خانوادگی خود را وارد نمایید.");
        return;
      }
      if (
        this.info.phone.trim() === "" &&
        this.aiChatConfig?.hasAssistantsMobile &&
        this.isPhone(this.info.phone.trim())
      ) {
        this.$toast.warning("شماره موبایل وارد شده نامعتبر است یا خالی است.");
        return;
      }
      this.loading = true;

      let rgx = new RegExp(/[۰-۹]+/g);
      if (rgx.test(this.info.phone))
        this.info.phone = this.english(this.info.phone);

      this.$api
        .post(`${this.$aiApi}/assistant/campaign`, {
          phone: this.info.phone,
          name: this.info.name,
          lastName: this.info.lastName,
        })
        .then(({ data }) => {
          localStorage.setItem(`ccsForce_${this.uniq_key}`, "optional");
          localStorage.setItem(`loginForce_${this.uniq_key}`, "true");

          this.showlogin = false;
        })
        .catch(({ response }) => {
          console.log("faield to save contact: ", response);
          this.showlogin = false;
        })
        .finally(() => {
          this.showlogin = false;
          this.loading = false;
        });
    },

    getTextDirection(text) {
      return this.detectLanguage(text);
    },

    getTextAlign(text) {
      const direction = this.detectLanguage(text);
      return direction === "rtl" ? "right" : "left";
    },
    detectLanguage(text) {
      const persianRegex = /[\u0600-\u06FF\u0750-\u077F]/;
      const arabicRegex = /[\u0600-\u06FF]/;
      const englishRegex = /[a-zA-Z]/;

      const persianCount = (text?.match(persianRegex) || []).length;
      const arabicCount = (text?.match(arabicRegex) || []).length;
      const englishCount = (text?.match(englishRegex) || []).length;

      if (persianCount > englishCount) {
        return "rtl";
      } else if (englishCount > 0) {
        return "ltr";
      }
      const rtlLanguages = ["fa", "ar", "ar_ae", "ar_iq"];
      return rtlLanguages.includes(this.currentlanguage) ? "rtl" : "ltr";
    },
    cancelRequest() {
      this.loading = false;
      this.isTyping = false;
      this.typingStopped = true;

      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = null;
      }

      const partialText = this.typingText.trim();
      // this.typingText = "";

      if (this.messages.length > 0) {
        const lastMessage = this.messages[this.messages.length - 1];

        if (lastMessage.role === "assistant") {
          if (partialText) {
            console.log(this.messages, "this.messages");
            this.$set(
              this.messages[this.messages.length - 1],
              "content",
              partialText + " [متوقف شد]",
            );
          } else {
            this.messages.pop();
          }
        } else if (lastMessage.role === "user") {
          this.messages.push({
            role: "assistant",
            content: partialText
              ? partialText + " [متوقف شد]"
              : "پاسخ متوقف شد.",
            timestamp: new Date(),
          });
        }
      }
      this.typingText = "";
      this.$nextTick(() => this.scrollToBottom());
    },
    async saveUserInteraction(message) {
      // hasAssistantsName: false,
      //       hasAssistantsFamily: false,
      //       hasAssistantsMobile: false,
      // ارسال به سیستم باشگاه مشتریان
    },
    typeText(fullText, messageIndex) {
      if (this.typingTimeout) clearTimeout(this.typingTimeout);

      this.isTyping = true;
      this.typingText = "";
      this.typingStopped = false;
      let currentIndex = 0;

      const typeNextChar = () => {
        if (this.typingStopped) {
          if (this.typingText.trim() && messageIndex < this.messages.length) {
            this.$set(
              this.messages[messageIndex],
              "content",
              this.typingText.trim(),
            );
          }
          this.isTyping = false;
          this.typingText = "";
          this.typingTimeout = null;
          this.$nextTick(() => this.scrollToBottom());

          return;
        }

        if (currentIndex < fullText.length) {
          this.typingText = fullText.substring(0, currentIndex + 1);
          currentIndex++;
          this.typingTimeout = setTimeout(typeNextChar, this.typingSpeed);
          this.$nextTick(() => this.scrollToBottom());
        } else {
          if (messageIndex < this.messages.length) {
            this.$set(this.messages[messageIndex], "content", fullText);
          }
          this.isTyping = false;
          this.typingText = "";
          this.typingTimeout = null;
          this.$nextTick(() => this.scrollToBottom());
        }
      };

      typeNextChar();
    },
    typeWriter() {
      if (this.index < this.fullText.length) {
        this.displayedText += this.fullText.charAt(this.index);
        this.index++;
        setTimeout(this.typeWriter, this.typingSpeed);
      }
    },
    openChat() {
      this.chatOpen = true;
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    async sendMessage() {
      if (!this.userMessage.trim() || this.loading) return;

      const userMsg = this.userMessage.trim();
      this.userMessage = "";

      this.messages.push({
        role: "user",
        content: userMsg,
        timestamp: new Date(),
      });

      this.loading = true;
      this.typingStopped = false;
      this.scrollToBottom();

      const assistantMessageIndex =
        this.messages.push({
          role: "assistant",
          content: "",
          timestamp: new Date(),
        }) - 1;

      try {
        const { data } = await this.$api.post(
          `${this.$aiApi}/assistant/chat/${this.uniq_key}`,
          {
            message: userMsg,
            previse_messages: this.previse_messages,
          },
        );

        if (this.typingStopped) {
          if (this.messages[assistantMessageIndex]?.content === "") {
            this.messages.splice(assistantMessageIndex, 1);
          }

          return;
        }

        let response =
          data?.output?.[0]?.content?.[0]?.text ||
          data?.choices?.[0]?.message?.content ||
          data?.text ||
          this.$t("AssistantChatTester.errorResponse");
        if (!this.typingStopped) {
          this.$nextTick(() => {
            this.typeText(response, assistantMessageIndex);
            this.scrollToBottom();
          });

          this.previse_messages.push(
            { role: "user", content: userMsg },
            { role: "assistant", content: response },
          );
        }
      } catch (err) {
        if (this.typingStopped) {
          if (this.messages[assistantMessageIndex]?.content === "") {
            this.messages.splice(assistantMessageIndex, 1);
          }
          return;
        }

        if (assistantMessageIndex < this.messages.length) {
          this.messages[assistantMessageIndex].content = this.$t(
            "AssistantChatTester.errorResponse",
          );
        }
      } finally {
        this.loading = false;
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    newChat() {
      this.$modal
        .yesNo(this.$t("AssistantChatTester.newChatConfirm"))
        .then((value) => {
          if (value === true) {
            this.clearChat();
          }
        });
    },

    clearChat() {
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = null;
      }
      this.messages = [];
      this.userMessage = "";
      this.typingText = "";
      this.isTyping = false;
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      this.loading = false;
    },

    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatContainer) {
          this.$refs.chatContainer.scrollTop =
            this.$refs.chatContainer.scrollHeight;
        }
      });
    },
    formatMessage(text) {
      if (!text) return "";

      let formattedText = text;

      formattedText = formattedText.replace(
        /\*\*(.*?)\*\*/g,
        "<strong>$1</strong>",
      );

      formattedText = formattedText.replace(/\*(.*?)\*/g, "<em>$1</em>");

      formattedText = formattedText.replace(
        /^(\d+\.)\s/gm,
        '<strong class="list-number">$1</strong> ',
      );

      formattedText = formattedText.replace(
        /^-\s/gm,
        '<span class="bullet-point">•</span> ',
      );

      formattedText = formattedText.replace(/\n/g, "<br>");

      return formattedText
        .replace(/\n/g, "<br>")
        .replace(
          /(https?:\/\/[^\s]+)/g,
          '<a href="$1" target="_blank" rel="noopener">$1</a>',
        );
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      const lang = this.currentlanguage || "fa-IR";
      return date.toLocaleTimeString(lang, {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatTime(ts) {
      return new Date(ts).toLocaleTimeString("fa-IR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const c = this.$refs.chatContainer;
        if (c) c.scrollTop = c.scrollHeight;
      });
    },

    fetch() {
      this.$api
        .get(`${this.$aiApi}/assistant/design/${this.uniq_key}`)
        .then((data) => {
          if (
            data?.data?.design &&
            typeof data.data.design === "object" &&
            Object.keys(data.data.design).length > 0
          ) {
            this.aiChatConfig = data?.data.design;
            this.showlogin = this.requiresLogin;
          }
        })
        .catch(() => {
          this.$toast.error(
            "خطایی در بازگذاری  چت هوشمند رخ داده لطفا با شتیبانی تماس بکیرید.",
          );
        })
        .finally(() => {
          Promise.all([
            this.$attrsuseImageLogo ? this.preloadImage(this.chatLogoSrc) : "",
            this.chatSettings.useImageBackground &&
            this.chatSettings.backgroundImage
              ? this.preloadImage(this.chatSettings.backgroundImage)
              : "",
          ])
            .then(() => {})
            .catch(() => {})
            .finally(() => {
              setTimeout(() => {
                this.isPageLoading = false;
              }, 10000);
            });
        });
    },
  },
};
</script>
<style scoped>
.preloader-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.chat-messages-area::-webkit-scrollbar {
  width: 6px;
}
.chat-messages-area::-webkit-scrollbar-thumb {
  background: var(--tail-color);
  border-radius: 10px;
}
.chat-messages-area::-webkit-scrollbar-thumb:hover {
  background: #555;
}
/* .pulse-wave {
  animation: pulseWave 1.8s ease-in-out infinite;
  box-shadow: 0 0 0 0 var(--pulse-color);
}
@keyframes pulseWave {
  0% {
    transform: scale(0.8);
    box-shadow: 0 0 0 0 var(--pulse-color);
  }
  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 25px transparent;
  }
  100% {
    transform: scale(0.8);
  }
} */
.chat-container-main {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  font-family: "Tahoma", sans-serif;
}
.desktop-bg-blur {
  position: absolute;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(15px);
  z-index: -1;
}

.chat-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  overflow: hidden;
  direction: rtl !important;
}
.chat-panel.desktop-mode {
  width: 85vh !important;
  height: 80vh !important;
  max-width: 1000px;
  border-radius: 25px !important;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4) !important;
  border: 1px solid rgba(255, 255, 255, 0.336);
  justify-self: center !important;
}

.chat-header {
  height: 70px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.online-dot {
  position: relative;
  width: 10px;
  height: 10px;
  background: #57f75c; /* سبز اصلی */
  border-radius: 50%;
  border: 2px solid #ffffff; /* حاشیه سفید برای کنتراست بهتر */
  box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); /* شروع هاله */
  animation: pulse 2s infinite ease-out, glow 2s infinite ease-in-out;
}

/* انیمیشن پالس (بزرگ و کوچک شدن + محو شدن هاله) */
@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    transform: scale(1.3);
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

/* انیمیشن چشمک/نفس کشیدن هاله سبز */
@keyframes glow {
  0%,
  100% {
    box-shadow: 0 0 4px 2px rgba(76, 175, 80, 0.6),
      0 0 12px 4px rgba(144, 168, 145, 0.6);
  }
  50% {
    box-shadow: 0 0 8px 4px rgba(76, 175, 80, 0.9),
      0 0 20px 8px rgba(150, 172, 151, 0.9);
  }
}
.chat-body-flex {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}
.chat-messages-area {
  flex: 1;
  overflow-y: auto;
  scroll-behavior: smooth;
  padding: 15px !important;
  direction: ltr;
  background: transparent !important;
}

.message-row {
  display: flex;
  width: 100%;
  align-items: flex-end;
  gap: 4px;
  margin-bottom: 1.5rem;
}
.user-side {
  flex-direction: row;
  justify-content: flex-end;
}
.assistant-side {
  flex-direction: row;
  justify-content: flex-start;
}

.msg-bubble {
  padding: 12px 16px;
  position: relative;
  max-width: 70%;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  width: fit-content;
  box-sizing: border-box;
}

.assistant-bubble {
  /* display: flex;
  flex-direction: row;
  gap: 1px !important; */
  border-radius: 18px 18px 18px 2px !important;
  /* margin-right: 15px;
  margin-left: 10px; */
  justify-self: start;
  margin-left: 0 !important;
  margin-right: 0 !important;
}
.user-bubble::after {
  content: "";
  position: absolute;
  bottom: 0;
  right: -8px;
  width: 10px;
  height: 15px;
  background: inherit;
  clip-path: polygon(0 0, 0% 100%, 100% 100%);
  background: var(--tail-color) !important;
}

.user-bubble {
  /* display: flex;
  flex-direction: row;
  gap: 1px !important; */
  border-radius: 18px 18px 2px 18px !important;
  /* margin-left: 15px;
  margin-right: 10px; */
  justify-self: end;
  margin-left: 0 !important;
  margin-right: 0 !important;
}
.assistant-bubble::before {
  content: "";
  position: absolute;
  bottom: 0;
  left: -8px;
  width: 15px;
  height: 15px;
  background: inherit;
  clip-path: polygon(100% 0, 0 100%, 100% 100%);
  background: var(--tail-color) !important;
}

.msg-footer {
  font-size: 10px;
  opacity: 0.7;
  margin-top: 5px;
  text-align: left;
}
.chat-footer-input {
  padding: 15px 30px;
  background: transparent !important;
  backdrop-filter: brightness(0.9) blur(9px) !important;
  padding-top: 15px !important;
}
.input-glass-box {
  display: flex;
  align-items: center;
  background: white;
  border-radius: 50px;
  padding: 5px 10px 5px 20px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
}

.typing-cursor {
  display: inline-block;
  width: 0.6em;
  text-align: left;
  animation: blink 1.1s step-end infinite;
  font-weight: normal;
  color: inherit;
  vertical-align: baseline;
  margin-left: 1px;
}
@keyframes blink {
  from,
  to {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}
.avatar-shadow {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  border: 2px solid white;
}
.closerobotbutton {
  z-index: 1000;
  animation: pulse 2s infinite;
  margin: 0 0 0 0 !important;
  padding: 0px, 0px, 0px, 0px !important;
  bottom: 220px !important;
  padding: 10px !important;
  margin: 0px !important;
  width: 15px !important;
  height: 15px !important;
}
.floating-chat-button {
  position: absolute !important;
  z-index: 15;
  animation: pulse 2s infinite;
  bottom: 35px !important;
  right: 12px !important;
  left: auto !important;
  width: 44px !important;
  height: 44px !important;
  pointer-events: auto;
}
.floating-chat-button:hover,
.floating-chat-button:active {
  transform: scale(1.1);
}
.chat-panel {
  position: absolute !important;
  bottom: 0px !important;
  left: 0px !important;
  right: 0px !important;
  height: 100% !important;
  max-height: 100% !important;
  min-height: 100% !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;
  z-index: 9999 !important;
  background: white !important;
}

.user-message::after {
  content: "";
  position: absolute;
  bottom: 3px;
  right: -12px;
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 14px solid #764ba2;
  border-right: 0;
}
.assistant-message::after {
  content: "";
  position: absolute;
  bottom: 8px;
  left: -12px;
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-right: 14px solid #f5f5f5;
  border-left: 0;
}
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 var(--pulse-color-with-alpha, rgba(102, 126, 234, 0.7));
  }
  70% {
    box-shadow: 0 0 0 8px var(--pulse-color-with-alpha, rgba(102, 126, 234, 0));
  }
  100% {
    box-shadow: 0 0 0 0 var(--pulse-color-with-alpha, rgba(102, 126, 234, 0));
  }
}
.chat-header {
  color: white;
  flex-shrink: 0;
  position: relative;
  color: white !important;
  min-height: 60px !important;
  flex-shrink: 0 !important;
  display: flex !important;
  align-items: center !important;
  z-index: 100 !important;
  backdrop-filter: brightness(0.8);
}

.user-message {
  color: white;
  /* border-radius: 18px 18px 4px 18px; */
  border-radius: 18px 18px 8px 18px !important;
  /* margin-right: 20px; */
  position: relative;
}

.assistant-message {
  background: #f5f5f5;
  /* border-radius: 18px 18px 18px 4px; */
  border-radius: 18px 18px 18px 10px !important;
  margin-left: 18px;
  position: relative;
  border: 1px solid #e0e0e0;
}

.message-content {
  word-wrap: break-word;
  white-space: pre-wrap;
  unicode-bidi: plaintext;
  left: 85px !important;
  right: auto;
}
.message-content[dir="rtl"] {
  text-align: right;
}

.message-content[dir="ltr"] {
  text-align: left;
}

.chat-messages {
  /* background: #ffffff;
  flex-shrink: 0; */
  flex: 1;
  overflow-y: auto;
  /* height: 100vh;
  max-height: 100vh; */
  padding: 16px;
}

.chat-input {
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
  min-height: 80px;
  backdrop-filter: brightness(0.9) blur(9px);
  padding-top: 15px !important;
}
.cssdialg {
  max-width: 450px;
  position: fixed;
  bottom: 155px;
  left: 0px;
}
.chat-dialog-custom {
  align-self: flex-end;
  margin: 0 0 155px 0 !important;
  max-width: 450px;
  border-radius: 8px;
}
.typing-text {
  color: #333;
  white-space: pre-wrap;
}
.typing-cursor {
  display: inline-block;
  animation: blink 1s infinite;
  font-weight: bold;
  color: #333;
  margin-right: 2px;
}

@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}
.chat {
  border-radius: 20px !important;
}
.chat-dialog-custom >>> .v-card {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.v-dialog__content {
  align-self: flex-end !important;
  margin-bottom: 155px !important;
}

.chat-dialog-custom {
  border-radius: 20px !important;
  overflow: hidden !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2) !important;
  margin: 0 !important;
}

.chat-dialog-custom >>> .v-card {
  border-radius: 20px !important;
  overflow: hidden;
}

.chat-dialog-custom >>> .chat-header {
  border-top-left-radius: 20px !important;
  border-top-right-radius: 20px !important;
}

.chat-dialog-custom >>> .chat-input {
  border-bottom-left-radius: 20px !important;
  border-bottom-right-radius: 20px !important;
}
.chat-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 14;
}
.stylelogin {
  color: inherit !important;
  background: inherit !important;
}
.message-rtl {
  direction: rtl;
}

.message-ltr {
  direction: ltr;
}

.message-rtl .message-content {
  text-align: right;
}

.message-ltr .message-content {
  text-align: left;
}
.message-content {
  word-wrap: break-word;
  white-space: pre-wrap;
  unicode-bidi: plaintext;
}
.message-content[dir="rtl"] {
  text-align: right;
}

.message-content[dir="ltr"] {
  text-align: left;
}
.login-card {
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  margin: 8px auto;
}

.login-card .v-input__slot {
  border-radius: 50px !important;
}
:deep(.v-input) {
  border-radius: 50px !important;
}
.bubble-container {
  /* display: flex !important; */
  flex: 1;
  min-width: 0px;
  max-width: 70%;
}
@media (max-width: 960px) {
  .msg-bubble {
    max-width: 80%;
  }
}
.pulse-wave.rainbow {
  position: relative;
  animation: pulseWave 2s ease-in-out infinite;
  box-shadow: 0 0 0 0 transparent;
}

.pulse-wave.rainbow::before {
  content: "";
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  border-radius: 50%;
  background: conic-gradient(
    from var(--angle, 0deg),
    #ff0000,
    #ff7f00,
    #ffff00,
    #00ff00,
    #0000ff,
    #4b0082,
    #ee82ee,
    #ff0000
  );
  filter: blur(8px);
  animation: rotateGradient 4s linear infinite,
    pulseGlow 2s ease-in-out infinite;
  z-index: -1;
  opacity: 0.8;
}

@keyframes rotateGradient {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes pulseGlow {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.6;
  }
}

@keyframes pulseWave {
  0% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
  }
  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 20px transparent;
  }
  100% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 transparent;
  }
}
.global-bg {
  display: none !important;
}
.modern-preloader-overlay {
  position: absolute;
  inset: 0;
  background: rgb(254, 255, 255);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
}

.modern-preloader-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.avatar-ripple-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 150px;
  height: 150px;
}

.preloader-avatar {
  position: relative;
  z-index: 10;
  background: var(--pulse-color, #667eea) !important;
  border: 3px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

/* افکت موج‌های مدرن (Ripple) */
.ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: var(--pulse-color, #667eea);
  opacity: 0;
  z-index: 1;
  animation: modernRipple 2.5s cubic-bezier(0.21, 0.53, 0.56, 0.8) infinite;
}

.ripple-1 {
  width: 90px;
  height: 90px;
  animation-delay: 0s;
}
.ripple-2 {
  width: 90px;
  height: 90px;
  animation-delay: 0.8s;
}
.ripple-3 {
  width: 90px;
  height: 90px;
  animation-delay: 1.6s;
}

@keyframes modernRipple {
  0% {
    width: 90px;
    height: 90px;
    opacity: 0.8;
  }
  100% {
    width: 250px;
    height: 250px;
    opacity: 0;
  }
}

/* استایل متن لودینگ */
.loading-text {
  color: rgb(17, 0, 0);
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  animation: fadePulse 2s infinite alternate;
}

.loading-dots::after {
  content: "";
  display: inline-block;
  animation: dotsLoop 1.5s steps(4, end) infinite;
}

@keyframes fadePulse {
  0% {
    opacity: 0.7;
    transform: translateY(0);
  }
  100% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

@keyframes dotsLoop {
  0%,
  20% {
    content: "";
  }
  40% {
    content: ".";
  }
  60% {
    content: "..";
  }
  80%,
  100% {
    content: "...";
  }
}
.assistant-bubble strong {
  font-weight: 800;
  color: #1a1a1a;
}

.assistant-bubble span {
  line-height: 1.8;
}

.list-number {
  color: #1976d2;
  margin-left: 4px;
}

.bullet-point {
  color: #1976d2;
  font-size: 1.2em;
  margin-left: 4px;
}
.login-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  overflow-y: auto;
}

.login-card-modern {
  transition: transform 0.3s ease;
}

.login-avatar {
  border: 4px solid rgba(255, 255, 255, 0.6) !important;
  background-color: white !important;
}

/* تنظیمات فیلدهای کپسولی */
::v-deep .modern-input .v-input__slot {
  border-radius: 50px !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.04) !important;
  min-height: 54px !important;
  padding: 0 20px !important;
}

::v-deep .modern-input input {
  font-size: 1.05rem !important;
  color: #333 !important;
}

::v-deep .modern-input .v-text-field__details {
  padding-right: 20px !important;
  margin-bottom: 4px !important;
}

::v-deep .modern-input .v-messages__message {
  color: #ffcccc !important; /* رنگ خطای ولیدیشن روی پس زمینه رنگی */
  font-weight: bold;
}

/* استایل دکمه شروع */
.start-chat-btn {
  font-size: 1.15rem !important;
  letter-spacing: 0px !important;
  transition: all 0.3s ease;
  height: 56px !important;
}

.start-chat-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 255, 255, 0.3) !important;
}

.opacity-80 {
  opacity: 0.8;
}
</style>
